import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useThemeStore } from '@/stores/theme-store';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { FormField, useFieldValidation, validators } from '@/components/ui/form-field';
import { cn } from '@/lib/utils';
import { CountdownTimer } from '@/components/ui/countdown-timer';
import { apiClient } from '@/lib/api';
import { Loader2, Sparkles, Sun, Moon, Shield, ArrowLeft, Mail } from 'lucide-react';

export function LoginPage() {
  useDocumentTitle('Sign In');

  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    remaining: number;
    limit: number;
    resetAt: number | undefined;
  } | null>(null);

  // Banner shown when Google OAuth redirects back with a denial (deactivated
  // account, 2FA policy, untrusted device) — see the /google/callback redirects.
  useEffect(() => {
    const googleError = searchParams.get('googleError');
    if (googleError) {
      const messages: Record<string, string> = {
        deactivated: 'Your account has been deactivated. Please contact support to restore access.',
        two_factor_policy: 'This portal requires two-factor authentication. Please sign in with your password.',
        '2fa_required': 'Two-factor authentication is enabled on your account. Please sign in with your password to complete verification.',
        error: 'Sign in with Google failed. Please try again or use your password.',
      };
      setError(messages[googleError] || messages.error);
    }
  }, [searchParams]);

  // Inline validation
  const emailValidation = useFieldValidation([validators.required(), validators.email()]);
  const passwordValidation = useFieldValidation([validators.required('Password is required')]);
  const { login, isLoading, user, isAuthenticated, sessionExpired } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      toast({
        title: t('auth.welcomeBack'),
        description: t('auth.signedInAs', { name: user.name || user.email }),
        variant: 'success',
      });
    }
  }, [isAuthenticated, user]);

  // 2FA verification state
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorVerifying, setTwoFactorVerifying] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const { verifyTwoFactor, pendingTwoFactorUserId } = useAuthStore();

  // 2FA recovery state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState<'password' | 'sent'>('password');
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  // Watch for 2FA requirement from the login response
  useEffect(() => {
    if (pendingTwoFactorUserId && !showTwoFactor) {
      setShowTwoFactor(true);
      setError('');
    }
  }, [pendingTwoFactorUserId]);

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorCode.trim() || !pendingTwoFactorUserId) return;

    setTwoFactorVerifying(true);
    setError('');

    try {
      await verifyTwoFactor(pendingTwoFactorUserId, twoFactorCode.trim(), rememberDevice);
      setShowTwoFactor(false);
      toast({
        title: t('auth.welcomeBack'),
        description: t('auth.signedInAs', { name: user?.name || email }),
        variant: 'success',
      });
      navigate('/');
    } catch (err: any) {
      const message = err?.message || 'Invalid verification code';
      setError(message);
      toast({ title: 'Verification failed', description: message, variant: 'destructive' });
    } finally {
      setTwoFactorVerifying(false);
    }
  };

  // ── 2FA Recovery Handlers ────────────────────────────────────
  const handleRecoveryRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recoveryPassword || !pendingTwoFactorUserId) return;

    setRecoveryLoading(true);
    setRecoveryError('');

    try {
      await apiClient.post('/auth/2fa/recovery/request', {
        userId: pendingTwoFactorUserId,
        password: recoveryPassword,
      });
      setRecoveryStep('sent');
    } catch (err: any) {
      setRecoveryError(err?.message || 'Failed to send recovery code');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleRecoveryVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recoveryCode.trim() || !pendingTwoFactorUserId) return;

    setRecoveryLoading(true);
    setRecoveryError('');

    try {
      const res = await apiClient.post<any>('/auth/2fa/recovery/verify', {
        userId: pendingTwoFactorUserId,
        code: recoveryCode.trim(),
      });
      if (res.success && res.data) {
        useAuthStore.setState({
          token: res.data.token,
          refreshToken: res.data.refreshToken,
          user: res.data.user,
          isAuthenticated: true,
          pendingTwoFactorUserId: null,
        });
        toast({
          title: 'Account recovered',
          description: `Welcome back, ${res.data.user.name || res.data.user.email}.`,
          variant: 'success',
        });
        navigate('/');
      }
    } catch (err: any) {
      setRecoveryError(err?.message || 'Invalid recovery code');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleBackFromRecovery = () => {
    setShowRecovery(false);
    setRecoveryStep('password');
    setRecoveryPassword('');
    setRecoveryCode('');
    setRecoveryError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await login(email, password);
      // Use getState() to read the FRESH value of pendingTwoFactorUserId (not the closure-captured one)
      const state = useAuthStore.getState();
      if (!state.pendingTwoFactorUserId) {
        navigate('/');
      }
    } catch (err: any) {
      const message = err?.message || t('auth.loginFailed');
      const isRateLimited = err.rateLimitRemaining !== undefined;

      if (isRateLimited) {
        setRateLimitInfo({
          remaining: err.rateLimitRemaining,
          limit: err.rateLimitLimit || 5,
          resetAt: err.rateLimitReset,
        });
      }

      setError(message);
      // Only show a toast for non-rate-limit errors — the inline rate limit indicator
      // is already visible and the toast just creates noise (and can double-fire).
      if (!isRateLimited) {
        toast({ title: t('auth.signInFailed'), description: message, variant: 'destructive' });
      }
    }
  };

  const handleBackToLogin = () => {
    setShowTwoFactor(false);
    setTwoFactorCode('');
    useAuthStore.setState({ pendingTwoFactorUserId: null });
  };

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4 overflow-x-hidden overflow-y-auto">
      {/* ===== Animated Background ===== */}

      {/* Floating gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Orb 1 — large green/teal */}
        <div
          className="absolute -top-32 -left-32 w-[350px] sm:w-[500px] h-[350px] sm:h-[500px] rounded-full opacity-[0.06] dark:opacity-[0.08] animate-float-slow"
          style={{
            background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
            animationDelay: '0s',
          }}
        />
        {/* Orb 2 — medium blue/purple */}
        <div
          className="absolute top-1/3 -right-40 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full opacity-[0.05] dark:opacity-[0.07] animate-float"
          style={{
            background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)',
            animationDelay: '-2s',
          }}
        />
        {/* Orb 3 — small amber/pink */}
        <div
          className="absolute -bottom-20 left-1/4 sm:left-1/3 w-[250px] sm:w-[350px] h-[250px] sm:h-[350px] rounded-full opacity-[0.04] dark:opacity-[0.06] animate-float-slow"
          style={{
            background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)',
            animationDelay: '-4s',
          }}
        />
        {/* Orb 4 — tiny cyan accent */}
        <div
          className="absolute top-2/3 left-1/4 w-[150px] sm:w-[200px] h-[150px] sm:h-[200px] rounded-full opacity-[0.03] dark:opacity-[0.05] animate-float"
          style={{
            background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)',
            animationDelay: '-6s',
          }}
        />
        {/* Decorative grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015] dark:opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='%2310b981' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Theme toggle — floating top-right */}
      <div className="fixed top-3 right-3 sm:top-4 sm:right-4 z-20 animate-stagger-in animate-stagger-in-delay-1">
        <button
          onClick={toggleTheme}
          className="p-2.5 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-dyndev-green/30 transition-all duration-300 hover:shadow-md hover:shadow-dyndev-green/5 group"
          title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          <div className="relative w-5 h-5">
            <Sun
              size={20}
              className={`absolute inset-0 transition-all duration-500 ${
                theme === 'dark'
                  ? 'opacity-100 rotate-0 scale-100'
                  : 'opacity-0 rotate-90 scale-75'
              }`}
            />
            <Moon
              size={20}
              className={`absolute inset-0 transition-all duration-500 ${
                theme === 'light'
                  ? 'opacity-100 rotate-0 scale-100'
                  : 'opacity-0 -rotate-90 scale-75'
              }`}
            />
          </div>
          <span className="sr-only">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
        </button>
      </div>

      {/* Noise overlay — subtle animated grain */}
      <div className="bg-noise opacity-[0.015] pointer-events-none fixed inset-0 animate-[noise-shift_0.5s_steps(5)_infinite]" />

      {/* ===== Content ===== */}
      <div className="w-full max-w-md relative z-10 py-8">
        {/* Logo — with shimmer */}
        <div className="text-center mb-8 animate-stagger-in animate-stagger-in-delay-1">
          <h1 className="text-4xl font-bold tracking-tight mb-2 relative inline-block group">
            <span className="relative">
              Dyn<span className="text-dyndev-green relative">
                Dev
                <span className="absolute inset-0 logo-shimmer rounded" />
              </span>
            </span>
            {/* Subtle sparkle icon that appears on hover */}
            <Sparkles
              size={16}
              className="absolute -top-2 -right-7 text-dyndev-green/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            />
          </h1>
          <p className="text-[var(--text-secondary)] text-sm">{t('app.tagline')}</p>
        </div>

        {/* Login Card — with staggered entrance */}
        <Card className="shadow-xl animate-stagger-in animate-stagger-in-delay-2 relative overflow-hidden">
          {/* Card shimmer border line on hover */}
          <div className="absolute inset-0 rounded-xl opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, transparent 40%, rgba(16,185,129,0.08) 50%, transparent 60%)',
              backgroundSize: '200% 200%',
              animation: 'shimmer 3s ease-in-out infinite',
            }}
          />
          <CardHeader>
            <CardTitle className="text-xl">{t('auth.signInTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {showTwoFactor ? (
              showRecovery ? (
                /* ===== 2FA Recovery Flow ===== */
                <div className="space-y-4">
                  {recoveryError && (
                    <div className="animate-stagger-in p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                      {recoveryError}
                    </div>
                  )}

                  {recoveryStep === 'password' && (
                    <form onSubmit={handleRecoveryRequest} className="space-y-4">
                      <div className="text-center mb-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
                          <Mail size={24} className="text-amber-400" />
                        </div>
                        <h3 className="text-base font-semibold mb-1">Account Recovery</h3>
                        <p className="text-xs text-[var(--text-secondary)]">
                          Enter your password to verify your identity and receive a recovery code via email.
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="recovery-password">Password</Label>
                        <Input
                          id="recovery-password"
                          type="password"
                          value={recoveryPassword}
                          onChange={(e) => setRecoveryPassword(e.target.value)}
                          placeholder="Enter your password"
                          autoFocus
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={recoveryLoading || !recoveryPassword}
                        size="pill"
                        className="w-full"
                      >
                        {recoveryLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {recoveryLoading ? 'Sending...' : 'Send Recovery Code'}
                      </Button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={handleBackFromRecovery}
                          className="text-xs text-[var(--text-secondary)] hover:text-dyndev-green transition-colors flex items-center gap-1 mx-auto"
                        >
                          <ArrowLeft size={12} />
                          Back to verification
                        </button>
                      </div>
                    </form>
                  )}

                  {recoveryStep === 'sent' && (
                    <form onSubmit={handleRecoveryVerify} className="space-y-4">
                      <div className="text-center mb-4">
                        <div className="w-12 h-12 rounded-xl bg-dyndev-green/10 flex items-center justify-center mx-auto mb-3">
                          <Mail size={24} className="text-dyndev-green" />
                        </div>
                        <h3 className="text-base font-semibold mb-1">Check Your Email</h3>
                        <p className="text-xs text-[var(--text-secondary)]">
                          We've sent a 6-digit recovery code to your email address. It expires in 10 minutes.
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="recovery-code">Recovery Code</Label>
                        <Input
                          id="recovery-code"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={recoveryCode}
                          onChange={(e) => setRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          className="font-mono text-center text-2xl tracking-[0.5em]"
                          autoFocus
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={recoveryLoading || recoveryCode.length !== 6}
                        size="pill"
                        className="w-full"
                      >
                        {recoveryLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {recoveryLoading ? 'Verifying...' : 'Verify & Sign In'}
                      </Button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => { setRecoveryStep('password'); setRecoveryError(''); setRecoveryCode(''); }}
                          className="text-xs text-[var(--text-secondary)] hover:text-dyndev-green transition-colors flex items-center gap-1 mx-auto"
                        >
                          <ArrowLeft size={12} />
                          Request a new code
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              ) : (
                /* ===== 2FA Verification Form ===== */
                <form onSubmit={handleTwoFactorSubmit} className="space-y-4">
                  {error && (
                    <div className="animate-stagger-in p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  <div className="text-center mb-4">
                    <div className="w-12 h-12 rounded-xl bg-dyndev-green/10 flex items-center justify-center mx-auto mb-3">
                      <Shield size={24} className="text-dyndev-green" />
                    </div>
                    <h3 className="text-base font-semibold mb-1">Two-Factor Authentication</h3>
                    <p className="text-xs text-[var(--text-secondary)]">
                      Enter the verification code from your authenticator app.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="2fa-code">Verification Code</Label>
                    <Input
                      id="2fa-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="font-mono text-center text-2xl tracking-[0.5em]"
                      autoFocus
                    />
                  </div>

                  {/* Remember this device */}
                  <label
                    className="flex items-center gap-2 cursor-pointer group"
                    onClick={() => setRememberDevice(!rememberDevice)}
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
                        rememberDevice
                          ? 'bg-dyndev-green border-dyndev-green'
                          : 'border-[var(--border-color)] group-hover:border-dyndev-green/50'
                      }`}
                    >
                      {rememberDevice && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <span className="text-xs text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors select-none">
                      Remember this device
                    </span>
                  </label>

                  <Button
                    type="submit"
                    disabled={twoFactorVerifying || twoFactorCode.length !== 6}
                    size="pill"
                    className="w-full"
                  >
                    {twoFactorVerifying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {twoFactorVerifying ? 'Verifying...' : 'Verify & Sign In'}
                  </Button>

                  {/* Recovery link */}
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setShowRecovery(true);
                        setRecoveryStep('password');
                        setRecoveryError('');
                      }}
                      className="text-xs text-[var(--text-secondary)] hover:text-dyndev-green transition-colors"
                    >
                      Lost access to your authenticator app?
                    </button>
                  </div>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleBackToLogin}
                      className="text-xs text-[var(--text-secondary)] hover:text-dyndev-green transition-colors flex items-center gap-1 mx-auto"
                    >
                      <ArrowLeft size={12} />
                      Back to sign in
                    </button>
                  </div>
                </form>
              )
            ) : (
              /* ===== Login Form ===== */
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Error message */}
                {error && (
                  <div className="animate-stagger-in animate-stagger-in-delay-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* Session-expired banner — shown after a forced logout when the
                    refresh token could no longer be exchanged (expired/revoked). */}
                {sessionExpired && (
                  <div className="animate-stagger-in animate-stagger-in-delay-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm">
                    {t('auth.sessionExpired')}
                  </div>
                )}

                {/* Rate limit indicator */}
                {rateLimitInfo && (
                  <div className="animate-stagger-in space-y-2">
                    {rateLimitInfo.remaining > 0 ? (
                      <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs flex items-center gap-2">
                        <div className="flex-1">
                          <span className="font-medium">{rateLimitInfo.remaining}</span>
                          <span className="text-[var(--text-secondary)]"> of </span>
                          <span className="font-medium">{rateLimitInfo.limit}</span>
                          <span className="text-[var(--text-secondary)]"> login attempts remaining</span>
                        </div>
                        {/* Mini progress bar */}
                        <div className="w-16 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden flex-shrink-0">
                          <div
                            className="h-full rounded-full bg-amber-400 transition-all duration-300"
                            style={{ width: `${(rateLimitInfo.remaining / rateLimitInfo.limit) * 100}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                        <span className="font-medium">Too many login attempts.</span>
                        <span className="text-[var(--text-secondary)]">
                          {' '}Try again in{' '}
                          {rateLimitInfo.resetAt ? (
                            <CountdownTimer
                              key={rateLimitInfo.resetAt}
                              resetAt={rateLimitInfo.resetAt}
                              onExpired={() => setRateLimitInfo(null)}
                            />
                          ) : (
                            'a few minutes'
                          )}
                          .
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Email field with inline validation */}
                <FormField
                  label={t('auth.email')}
                  error={emailValidation.error}
                  touched={emailValidation.touched}
                  dirty={emailValidation.dirty}
                  showError={emailValidation.showError}
                  isValid={emailValidation.isValid}
                  className="animate-stagger-in animate-stagger-in-delay-3"
                >
                  <div className={`relative rounded-lg transition-all duration-300 ${emailFocused ? 'ring-2 ring-dyndev-green/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : emailValidation.showError ? 'ring-2 ring-red-400/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}`}>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        emailValidation.onChange(e);
                        if (rateLimitInfo) setRateLimitInfo(null);
                      }}
                      onBlur={(e) => {
                        setEmailFocused(false);
                        emailValidation.onBlur();
                      }}
                      onFocus={() => setEmailFocused(true)}
                      placeholder={t('auth.emailPlaceholder')}
                      required
                      className={cn(
                        'transition-all duration-300',
                        emailValidation.showError && 'border-red-400/50 focus:border-red-400 focus:ring-red-400',
                        emailValidation.isValid && emailValidation.dirty && 'border-dyndev-green/50',
                      )}
                    />
                    <div
                      className={`absolute bottom-0 left-1/2 h-[2px] rounded-full transition-all duration-500 -translate-x-1/2 ${
                        emailValidation.showError ? 'w-3/4 opacity-100 bg-red-400' :
                        emailValidation.isValid && emailValidation.dirty ? 'w-3/4 opacity-100 bg-dyndev-green' :
                        'w-0 opacity-0'
                      }`}
                    />
                  </div>
                </FormField>

                {/* Password field with inline validation */}
                <FormField
                  label={t('auth.password')}
                  error={passwordValidation.error}
                  touched={passwordValidation.touched}
                  dirty={passwordValidation.dirty}
                  showError={passwordValidation.showError}
                  isValid={passwordValidation.isValid}
                  className="animate-stagger-in animate-stagger-in-delay-4"
                >
                  <div className={`relative rounded-lg transition-all duration-300 ${passwordFocused ? 'ring-2 ring-dyndev-green/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : passwordValidation.showError ? 'ring-2 ring-red-400/30 ring-offset-2 ring-offset-[var(--bg-primary)]' : ''}`}>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        passwordValidation.onChange(e);
                        if (rateLimitInfo) setRateLimitInfo(null);
                      }}
                      onBlur={(e) => {
                        setPasswordFocused(false);
                        passwordValidation.onBlur();
                      }}
                      onFocus={() => setPasswordFocused(true)}
                      placeholder={t('auth.passwordPlaceholder')}
                      required
                      className={cn(
                        'transition-all duration-300',
                        passwordValidation.showError && 'border-red-400/50 focus:border-red-400 focus:ring-red-400',
                        passwordValidation.isValid && passwordValidation.dirty && 'border-dyndev-green/50',
                      )}
                    />
                    <div
                      className={`absolute bottom-0 left-1/2 h-[2px] rounded-full transition-all duration-500 -translate-x-1/2 ${
                        passwordValidation.showError ? 'w-3/4 opacity-100 bg-red-400' :
                        passwordValidation.isValid && passwordValidation.dirty ? 'w-3/4 opacity-100 bg-dyndev-green' :
                        'w-0 opacity-0'
                      }`}
                    />
                  </div>
                </FormField>

                {/* Submit button */}
                <div className="animate-stagger-in animate-stagger-in-delay-5">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    size="pill"
                    className="w-full group relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-dyndev-green/20 active:scale-[0.98]"
                  >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s ease-in-out infinite',
                      }}
                    />
                    {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {isLoading ? t('auth.signingIn') : t('auth.signIn')}
                    {!isLoading && (
                      <span className="ml-2 opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0 inline-block">
                        →
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* Divider */ }
            {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
              <>
                <div className="flex items-center gap-3 my-6 animate-stagger-in animate-stagger-in-delay-5">
                  <Separator className="flex-1" />
                  <span className="text-sm text-[var(--text-secondary)]">{t('auth.or')}</span>
                  <Separator className="flex-1" />
                </div>

                <div className="animate-stagger-in animate-stagger-in-delay-6">
                  <Button variant="outline" className="w-full gap-3 transition-all duration-300 hover:border-dyndev-green/30 hover:shadow-md" asChild>
                    <a href="/client/api/auth/google">
                      <svg width="20" height="20" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {t('auth.continueWithGoogle')}
                    </a>
                  </Button>
                </div>
              </>
            )}

            {/* Links */}
            <div className="animate-stagger-in animate-stagger-in-delay-7">
              <p className="text-center mt-4 text-sm text-[var(--text-secondary)]">
                <Link to="/forgot-password" className="hover:text-dyndev-green transition-colors duration-200 relative group">
                  {t('auth.forgotPassword')}
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-dyndev-green group-hover:w-full transition-all duration-300" />
                </Link>
              </p>

              <p className="text-center mt-2 text-sm text-[var(--text-secondary)]">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-dyndev-green hover:text-dyndev-green/80 transition-all duration-200 font-medium relative group">
                  {t('auth.signUp')}
                  <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-dyndev-green group-hover:w-full transition-all duration-300" />
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center mt-6 text-xs text-[var(--text-secondary)] animate-stagger-in animate-stagger-in-delay-8">
          &copy; {new Date().getFullYear()} DynDev. {t('app.copyright')}
        </p>
      </div>
    </div>
  );
}
